import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { nightlyText, useNightly } from "../ai/AiSection";
import { useEngines } from "../ai/ai";
import { Link } from "react-router";
import { Ban, Mail, MessageCircle, NotebookPen, Phone } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useIsMobile } from "@/hooks/use-mobile";
import MobileHeader from "../../layout/MobileHeader";
import { MobileContent } from "../../layout/MobileContent";
import { getSupabaseClient } from "../../providers/supabase/supabase";
import {
  channelOf,
  mailHref,
  telHref,
  waHref,
  type Channel,
} from "../contactLinks";
import { useTemplateFor } from "../templates/useTemplateFor";
import { startCallTimer } from "../calls/callTimer";
import { QuickLog } from "./QuickLog";
import { SendPreview } from "../templates/SendPreview";
import { useDoneToday, useWeeklyGoal } from "./useWeeklyGoal";
import { OfferDot, StagePill } from "../ui";
import { offerColor } from "../useOffers";
import {
  AlertList,
  PriorityIcon,
  priScore,
  ThermoBar,
  useThermoAlerts,
} from "../thermo/thermo";

export type MyDayTask = {
  task_id: number;
  channel: string | null; // tasks.type
  text: string | null;
  due_date: string;
  contact_id: number;
  first_name: string | null;
  last_name: string | null;
  phone_jsonb: { number: string }[] | null;
  email_jsonb: { email: string }[] | null;
  company_id: number | null;
  company_name: string | null;
  city: string | null;
  deal_id: number | null;
  stage: string | null;
  offer_slug: string | null;
  deal_key: string | null;
  offer_id: number | null;
  offer_name: string | null;
  offer_priority: number | null;
  template_id: number | null;
  giro: string | null;
  dnc: boolean;
  thermo: number | null;
};

const GROUPS: { channel: Channel; title: string }[] = [
  { channel: "llamada", title: "Llamadas" },
  { channel: "whatsapp", title: "WhatsApp" },
  { channel: "email", title: "Correos" },
  { channel: "otro", title: "Otras tareas" },
];

// Si la app se recarga al volver de otra app (PWA), el panel se reabre
const PENDING_KEY = "raftel.quicklog.pending";

const useMyDay = () =>
  useQuery({
    queryKey: ["my_day"],
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("my_day")
        .select("*");
      if (error) throw error;
      // prioridad tipo Jira (termómetro ± prioridad del producto; sin oportunidad al final) y vencimiento
      const score = (t: MyDayTask) =>
        t.thermo == null ? -99 : priScore(t.thermo, t.offer_priority);
      return (data as MyDayTask[]).sort(
        (a, b) => score(b) - score(a) || a.due_date.localeCompare(b.due_date),
      );
    },
  });

export const MyDay = () => {
  const isMobile = useIsMobile();
  const { data, isPending, error } = useMyDay();
  const templateFor = useTemplateFor();
  const [logging, setLogging] = useState<{
    task: MyDayTask;
    channel: Channel;
  } | null>(null);

  useEffect(() => {
    if (!data || logging) return;
    const pending = JSON.parse(localStorage.getItem(PENDING_KEY) ?? "null");
    const task = pending && data.find((t) => t.task_id === pending.task_id);
    if (task) setLogging({ task, channel: pending.channel });
  }, [data, logging]);

  const startLog = (task: MyDayTask, channel: Channel) => {
    localStorage.setItem(
      PENDING_KEY,
      JSON.stringify({ task_id: task.task_id, channel }),
    );
    setLogging({ task, channel });
  };
  const endLog = () => {
    localStorage.removeItem(PENDING_KEY);
    setLogging(null);
  };

  const content = (
    <div className="flex flex-col gap-6 max-w-2xl mx-auto w-full">
      {!isMobile && <h2 className="text-2xl font-bold">Mi día</h2>}
      <Kpis tasks={data ?? []} />
      <SessionBanner />
      <AiNotices />
      <Alerts />
      {isPending ? (
        <Skeleton className="h-32 w-full" />
      ) : error ? (
        <p className="text-destructive">No se pudo cargar Mi día.</p>
      ) : !data?.length ? (
        <EmptyDay />
      ) : (
        GROUPS.map((g) => {
          const tasks = data.filter((t) => channelOf(t.channel) === g.channel);
          if (!tasks.length) return null;
          return (
            <section key={g.channel} className="flex flex-col gap-3">
              <h3 className="font-semibold">
                {g.title}{" "}
                <span className="text-muted-foreground">({tasks.length})</span>
              </h3>
              {tasks.map((t) => (
                <TaskCard
                  key={t.task_id}
                  task={t}
                  onAction={startLog}
                  templateFor={templateFor}
                />
              ))}
            </section>
          );
        })
      )}
      <div className="flex justify-center gap-2">
        <Button asChild variant="ghost">
          <Link to="/tasks">Ver todas las tareas</Link>
        </Button>
        {isMobile && (
          <Button asChild variant="ghost">
            <Link to="/tablero">Ver tablero</Link>
          </Button>
        )}
      </div>
      <QuickLog
        task={logging?.task ?? null}
        channel={logging?.channel ?? "otro"}
        onClose={endLog}
      />
    </div>
  );

  if (!isMobile) return content;
  return (
    <>
      <MobileHeader>
        <h1 className="text-xl font-semibold">Mi día</h1>
      </MobileHeader>
      <MobileContent>{content}</MobileContent>
    </>
  );
};

/** Cifras del día (PRD C.1) y meta semanal de demos con racha (C.5). */
const Kpis = ({ tasks }: { tasks: MyDayTask[] }) => {
  const { data: goal } = useWeeklyGoal();
  const { data: done } = useDoneToday();
  const kpi = (
    v: React.ReactNode,
    l: React.ReactNode,
    extra?: React.ReactNode,
  ) => (
    <Card className="gap-0.5 p-3">
      <span className="text-2xl font-extrabold tracking-tight">{v}</span>
      <span className="text-sm font-semibold text-muted-foreground">{l}</span>
      {extra}
    </Card>
  );
  const pct = goal
    ? Math.min(100, (goal.thisWeek / Math.max(1, goal.goal)) * 100)
    : 0;
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      {kpi(tasks.length, "Tareas pendientes")}
      {kpi(
        tasks.filter((t) => channelOf(t.channel) === "llamada").length,
        "Llamadas por hacer",
      )}
      {kpi(done ?? 0, "Hechas hoy")}
      {kpi(
        <>
          {goal?.thisWeek ?? 0}{" "}
          <span className="text-base text-muted-foreground">
            / {goal?.goal ?? 5}
          </span>
        </>,
        <>
          Demos esta semana · racha {goal?.streak ?? 0} semana
          {goal?.streak === 1 ? "" : "s"}
        </>,
        <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full"
            style={{
              width: `${pct}%`,
              background: pct >= 100 ? "var(--good)" : "var(--primary)",
            }}
          />
        </div>,
      )}
    </div>
  );
};

const SessionBanner = () => {
  const { data: goal } = useWeeklyGoal();
  const missing = goal ? Math.max(0, goal.goal - goal.thisWeek) : null;
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-dashed border-primary p-3.5">
      <div className="min-w-52 flex-1">
        <b>Sesión de llamadas</b>
        <p className="text-sm text-muted-foreground">
          Tus oportunidades más importantes en fila, con guion al lado y
          registro en un toque.
          {missing ? ` Te faltan ${missing} demos para tu meta.` : ""}
        </p>
      </div>
      <Button asChild>
        <Link to="/sesion">
          <Phone />
          Iniciar sesión
        </Link>
      </Button>
    </div>
  );
};

/**
 * Alertas del termómetro (se enfría, bajó, se calentó, caliente fuera del CRM):
 * las 5 primeras; el resto en la campana. Incluye quejas en aumento en reseñas (Bloque H).
 */
const Alerts = () => {
  const { data } = useThermoAlerts();
  if (!data?.length) return null;
  return (
    <Card className="gap-1 p-3">
      <h3 className="px-2 font-semibold">
        Alertas del termómetro{" "}
        <span className="text-muted-foreground">({data.length})</span>
      </h3>
      <AlertList alerts={data.slice(0, 5)} />
    </Card>
  );
};

/** Avisos de la IA: lo que investigó anoche y servicios en pausa por llegar al 90 % del mes. */
const AiNotices = () => {
  const { data: night } = useNightly();
  const { data: st } = useEngines();
  const text = nightlyText(night);
  const paused = (st ?? []).filter((x) => x.blocked);
  if (!text && !paused.length) return null;
  const names: Record<string, string> = {
    tavily: "Tavily",
    serpapi: "SerpApi",
    gemini_busqueda: "la búsqueda de Google de Gemini",
  };
  return (
    <Card className="gap-1 border-[var(--ai)] p-3 text-sm">
      {text && (
        <p>
          <b>{text}</b>{" "}
          <Link to="/investigaciones" className="text-primary hover:underline">
            Ver investigaciones
          </Link>
        </p>
      )}
      {paused.map((x) => (
        <p key={x.service} className="font-semibold text-[var(--critical)]">
          {names[x.service] ?? x.service} llegó al 90 % de su cupo del mes (
          {x.month_used} de {x.month_limit}): el CRM dejó de usarlo hasta el mes
          siguiente.
        </p>
      ))}
    </Card>
  );
};

const CH_COLOR: Record<string, string> = {
  llamada: "var(--ch-llamada)",
  whatsapp: "var(--ch-whatsapp)",
  email: "var(--ch-email)",
};

const EmptyDay = () => (
  <Card className="p-6 text-center flex flex-col gap-3 items-center">
    <p className="font-medium">No tienes pendientes para hoy.</p>
    <p className="text-sm text-muted-foreground">
      Aquí aparecen tus tareas vencidas y de hoy. Pasa empresas al CRM desde el
      Directorio y agenda la primera llamada.
    </p>
    <Button asChild variant="outline">
      <Link to="/directory">Ir al Directorio</Link>
    </Button>
  </Card>
);

const TaskCard = ({
  task,
  onAction,
  templateFor,
}: {
  task: MyDayTask;
  onAction: (task: MyDayTask, channel: Channel) => void;
  templateFor: ReturnType<typeof useTemplateFor>;
}) => {
  const phone = task.phone_jsonb?.[0]?.number;
  const email = task.email_jsonb?.[0]?.email;
  const wa = templateFor(task, "whatsapp");
  const mail = templateFor(task, "email");
  const guion = templateFor(task, "llamada");
  const main = channelOf(task.channel);
  const overdue = new Date(task.due_date) < new Date(new Date().toDateString());
  const actions: {
    channel: Channel;
    label: string;
    Icon: typeof Phone;
    href: string | null;
  }[] = [
    { channel: "llamada", label: "Llamar", Icon: Phone, href: telHref(phone) },
    {
      channel: "whatsapp",
      label: "WhatsApp",
      Icon: MessageCircle,
      href: waHref(phone, wa?.body),
    },
    {
      channel: "email",
      label: "Correo",
      Icon: Mail,
      href: mailHref(email, mail?.subject, mail?.body),
    },
  ];

  return (
    <Card className="p-4 gap-3">
      <div className="flex justify-between gap-2">
        <span
          className="grid size-9 shrink-0 place-items-center rounded-lg text-white"
          style={{ background: CH_COLOR[main] ?? "var(--st-nuevo)" }}
          aria-hidden
        >
          {main === "whatsapp" ? (
            <MessageCircle className="size-5" />
          ) : main === "email" ? (
            <Mail className="size-5" />
          ) : (
            <Phone className="size-5" />
          )}
        </span>
        <div className="min-w-0 flex-1">
          {task.deal_key && (
            <span className="font-mono text-xs text-muted-foreground">
              {task.deal_key}
            </span>
          )}
          <Link
            to={
              task.company_id
                ? `/companies/${task.company_id}/show`
                : `/contacts/${task.contact_id}/show`
            }
            className="font-semibold block truncate"
          >
            {task.company_name ?? `${task.first_name} ${task.last_name}`}
          </Link>
          <p className="text-sm text-muted-foreground truncate">
            {task.first_name} {task.last_name}
            {task.city ? ` · ${task.city}` : ""}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          {task.thermo != null && (
            <span className="flex items-center gap-1.5">
              <PriorityIcon
                thermo={task.thermo}
                offerPriority={task.offer_priority}
                withLabel
              />
              <ThermoBar value={task.thermo} mini />
            </span>
          )}
          {task.offer_name && (
            <Badge variant="outline" className="gap-1.5">
              <OfferDot color={offerColor({ slug: task.offer_slug ?? "" })} />
              {task.offer_name}
            </Badge>
          )}
          {task.stage && <StagePill stage={task.stage} />}
        </div>
      </div>
      {task.text && <p className="text-sm">{task.text}</p>}
      {guion && main === "llamada" && (
        <details className="text-sm">
          <summary className="cursor-pointer text-muted-foreground">
            Ver guion
            {guion.pending_validation && (
              <b className="text-foreground">
                {" "}
                · sin validar: no prometas lo que no esté confirmado
              </b>
            )}
          </summary>
          <p className="whitespace-pre-line mt-1">{guion.body}</p>
        </details>
      )}
      {overdue && (
        <p className="text-xs text-destructive">
          Vencida el{" "}
          {new Date(task.due_date).toLocaleDateString("es-MX", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          })}
        </p>
      )}
      {task.dnc ? (
        <p className="text-sm text-destructive flex items-center gap-2">
          <Ban className="size-4" /> En lista de no contactar: botones
          deshabilitados.
        </p>
      ) : null}
      <div className="grid grid-cols-4 gap-2">
        {actions.map(({ channel, label, Icon, href }) =>
          href && !task.dnc && channel !== "llamada" ? (
            // WhatsApp y correo: vista previa con los datos de la empresa antes de abrir
            <SendPreview
              key={channel}
              channel={channel as "whatsapp" | "email"}
              target={task}
              phone={phone}
              email={email}
              onSent={() => onAction(task, channel)}
              trigger={
                <Button
                  variant={channel === main ? "default" : "outline"}
                  className="h-12 flex-col gap-0 text-xs"
                >
                  <Icon />
                  {label}
                </Button>
              }
            />
          ) : href && !task.dnc ? (
            <Button
              key={channel}
              asChild
              variant={channel === main ? "default" : "outline"}
              className="h-12 flex-col gap-0 text-xs"
            >
              <a
                href={href}
                target={channel === "llamada" ? undefined : "_blank"}
                rel="noreferrer"
                onClick={() => {
                  if (channel === "llamada") startCallTimer();
                  onAction(task, channel);
                }}
              >
                <Icon />
                {label}
              </a>
            </Button>
          ) : (
            <Button
              key={channel}
              variant="outline"
              disabled
              className="h-12 flex-col gap-0 text-xs"
            >
              <Icon />
              {label}
            </Button>
          ),
        )}
        <Button
          variant="ghost"
          className="h-12 flex-col gap-0 text-xs"
          onClick={() => onAction(task, main)}
        >
          <NotebookPen />
          Registrar
        </Button>
      </div>
    </Card>
  );
};
